import { from, Subscription } from 'rxjs';
import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { add, sub } from 'date-fns';
import { Platform } from '@ionic/angular';
import { computed, observable } from 'mobx-angular';
import { lastObject, removeObject } from 'netsocial-lib/src/app/utils/array-utils';
import { ServiceService } from 'netsocial-posting/src/app/services/service/service.service';
import { TeamService } from 'netsocial-posting/src/app/services/team/team.service';
import {
  ApiRequestService,
  NetSocialAccountFields,
} from 'netsocial-lib/src/app/services/api-request/api-request.service';
import { Team } from 'netsocial-lib/src/app/interfaces/team';
import { ErrorService } from 'netsocial-posting/src/app/services/error/error.service';
import { ReportView } from '../../../services/reporting/reporting.service';
import { ServiceInfo } from 'netsocial-posting/src/app/interfaces/service-info';

export interface ChangeFiltersOutput {
  start: Date;
  end: Date;
  filterBy: ReportView;
  socials?: string[];
  compareResults: boolean;
  comparison: string;
  startCompare?: Date;
  endCompare?: Date;
  teamIds?: Array<Team['id']>;
}

@Component({
  selector: 'analytics-header',
  templateUrl: './analytics-header.component.html',
  styleUrls: ['./analytics-header.component.scss'],
})
export class AnalyticsHeaderComponent implements OnInit, OnDestroy {
  public isDesktop: boolean;

  public comparisonOptions = ['custom', 'oneYear', 'twoYear'];

  @Input() public start = sub(new Date(), { months: 6 });
  @Input() public end = new Date();

  @Input() public startCompareCustom = AnalyticsHeaderComponent.transformDate(
    this.start,
    lastObject(this.comparisonOptions)!
  );
  @Input() public endCompareCustom = AnalyticsHeaderComponent.transformDate(
    this.end,
    lastObject(this.comparisonOptions)!
  );

  @Input() public emitOnChange = false;

  @HostBinding('class.dialog-view')
  @Input()
  public dialogView: boolean;

  @Input() public filterBy: ReportView = 'company';
  @observable @Input() public socials: string[] = [];

  @Input() public compareResults: boolean;
  @observable @Input() public comparison = <string>lastObject(this.comparisonOptions);

  public contextOwner?: NetSocialAccountFields;
  public viewOptions = ['personal', 'company', 'organization'];

  @computed
  public get startCompare() {
    return AnalyticsHeaderComponent.transformDate(this.start, this.comparison);
  }

  @computed
  public get endCompare() {
    return AnalyticsHeaderComponent.transformDate(this.end, this.comparison);
  }

  @Output() public ionChangeFilters = new EventEmitter<ChangeFiltersOutput>();

  public teams?: Team[];
  public selectedTeams: Team[] = [];

  private contextOwnerSubscription: Subscription;

  private getServiceSubscription?: Subscription;
  private getTeamsSubscription?: Subscription;

  public services: string[];

  constructor(
    private platform: Platform,
    private serviceService: ServiceService,
    private teamService: TeamService,
    private apiRequest: ApiRequestService,
    private errorService: ErrorService
  ) {
    this.isDesktop = this.platform.is('desktop');
  }

  static transformDate(date: Date, comparison: string) {
    let value: Date | undefined;

    switch (comparison) {
      case 'twoYear':
        value = sub(date, { years: 2 });
        break;
      case 'oneYear':
        value = sub(date, { years: 1 });
        break;
      case 'sixMonths':
        value = sub(date, { months: 6 });
        break;
      case 'twoMonths':
        value = sub(date, { months: 2 });
        break;
    }

    return value;
  }

  formatFilterBy(filterBy: string) {
    return `components.analyticsHeader.filterBy.options.${filterBy}`;
  }

  ngOnInit() {
    this.filterResults();

    this.contextOwnerSubscription = this.apiRequest.getContextOwner().subscribe((contextOwner) => {
      this.contextOwner = contextOwner;
      this.viewOptions =
        this.contextOwner?.accountType === 'company'
          ? ['company', 'organization']
          : ['personal', 'company', 'organization'];
      if (this.contextOwner?.accountType === 'company' && this.filterBy === 'personal') {
        this.filterBy = 'company';
      }
      if (this.dialogView) {
        this.getTeams();
      }
      this.getServices();
    });
  }

  ngOnDestroy() {
    this.getServiceSubscription?.unsubscribe();
    this.getTeamsSubscription?.unsubscribe();
    this.contextOwnerSubscription.unsubscribe();
  }

  mutSocials(value: string, action: 'add' | 'remove') {
    if (action === 'add' && value === 'all') {
      this.socials = ['all'];
    } else if (action === 'add') {
      if (this.socials.includes('all')) {
        removeObject(this.socials, 'all');
      }

      this.socials = [...this.socials, value];
    } else if (action === 'remove') {
      removeObject(this.socials, value);
      if (!this.socials.length) {
        this.socials = ['all'];
      }
    }
  }

  didChange() {
    if (this.compareResults && this.startCompareCustom) {
      const startDatetime = new Date(this.start);
      const endDatetime = new Date(this.end);
      let dateDifference = endDatetime.valueOf() - startDatetime.valueOf();
      dateDifference = Math.floor(dateDifference / 1000);
      this.endCompareCustom = add(this.startCompareCustom, {
        seconds: dateDifference,
      });
    }

    if (this.emitOnChange) {
      this.filterResults();
    }
  }

  filterResults() {
    this.ionChangeFilters.emit({
      start: this.start,
      end: this.end,
      filterBy: this.filterBy,
      socials: this.socials.length ? this.socials.map((social) => social.toLowerCase()) : undefined,
      teamIds:
        this.selectedTeams.length && this.filterBy === 'organization'
          ? this.selectedTeams.map((team) => team.id)
          : undefined,
      compareResults: this.compareResults,
      comparison: this.comparison,
      startCompare: this.comparison === 'custom' ? this.startCompareCustom : this.startCompare,
      endCompare: this.comparison === 'custom' ? this.endCompareCustom : this.endCompare,
    });
  }

  private getTeams() {
    this.getTeamsSubscription?.unsubscribe();

    this.getTeamsSubscription = from(
      this.errorService.resolve(this.teamService.getTeams())
    ).subscribe((teams) => {
      this.teams = teams;
      this.getTeamsSubscription = undefined;
    });
  }

  private getServices() {
    this.getServiceSubscription?.unsubscribe();

    this.getServiceSubscription = from(
      this.errorService.resolve(
        this.serviceService.getServiceInfo({
          services: ['all-content'],
        })
      )
    ).subscribe({ next: this.removeExcludedServices.bind(this) });
  }

  removeExcludedServices = (services: ServiceInfo[]) => {
    const excludedServices = ['instagramold', 'default'];

    this.services = [
      ...(services ?? [])
        .map((service: ServiceInfo) => service.name)
        .filter((service: string) => !excludedServices.includes(service.toLowerCase())),
    ];
    this.getServiceSubscription = undefined;
  };
}
